/**
 * תצוגת מוצרים מתחלפת ב-hero — TASKS 6.2 · DESIGN.md 5.2.1.
 *
 * ⭐ דרישת אריאנה (12.08.2026): "תמונה נעה" של כל המוצרים הפעילים ב-hero.
 *    נשלף מ-`GET /api/products` (⛔ אין שדה מחיר בתגובה — `catalogApi.ts`),
 *    אותן תמונות שכבר קיימות בקטלוג, ⛔ לא נכס חדש.
 *
 * ⭐ עצירה אוטומטית ב-hover/focus + כפתור עצירה ידני (WCAG 2.2.2 — תוכן נע
 *    חייב מנגנון עצירה) + `prefers-reduced-motion`/כפתור הנגישות: קופאת על
 *    התמונה הראשונה, ⛔ בלי סיבוב בכלל — ⛔ לא רק דילוג על משכי האנימציה.
 *
 * ⭐ **חיצים ידניים — Feedback1.md סעיף 6.8ג (20.08.2026).** תוספת לדילוג
 *    האוטומטי הקיים (⛔ לא תחליף) — `‹`/`›` על התמונה, מודולו אורך המערך.
 *    לחיצה על חץ מפעילה מחדש את הטיימר האוטומטי (`resetKey`) כדי שהקרוסלה
 *    לא "תיתקע" מיד אחרי התערבות ידנית. קומפ הבחירה: `Hero_וריאציות.html`.
 *
 * ⚠️ ⛔ **בלי `layoutId`** — אם אי-פעם המוצר המוצג כאן מופיע גם בכרטיס קטלוג
 *    (`ProductCard.tsx`) על אותו מסך, שני `layoutId` זהים בו-זמנית מתנגשים.
 *    הניווט לדף המוצר הוא ניווט רגיל (מעבר עמוד fade, 6.5), ⛔ לא shared element.
 *
 * ⚠️ ⛔ הכניסה ההתחלתית (fade+y) של הרכיב **אינה** מטופלת כאן — היא חלק
 *    מה-stagger הכולל של ה-hero ב-`HomePage.tsx` (הרכיב הוא "תמונה אחרונה"
 *    ב-DESIGN.md 6.2). כאן מטופלים רק: קרוסלת הכרטיס עצמה והפרלקסה.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AnimatePresence,
  motion,
  useReducedMotionConfig,
  useScroll,
  useSpring,
  useTransform,
} from 'motion/react';
import { CaretLeft, CaretRight, Pause, Play } from '@phosphor-icons/react';
import {
  isPublicApiError,
  listProducts,
  type PublicProductListItem,
} from '../publicApi/catalogApi.js';

const ROTATE_INTERVAL_MS = 4000;
const CROSSFADE_SECONDS = 0.6;
const PARALLAX_RANGE_PX = 14;

export function HeroShowcase() {
  const [products, setProducts] = useState<PublicProductListItem[] | null>(null);
  // ⭐ מק"טים שהתמונה שלהם נכשלה בפועל בטעינה (404/רשת) - ⛔ לא רק "אין
  // שדה תמונה" (זה כבר מסונן למעלה ב-`listProducts().then`). קובץ תמונה
  // שבור בשרת גרר בעבר ריבוע לבן שקוף לגמרי (`alt=""`) בלי שום סימן חזותי -
  // כאן אנחנו פשוט מוציאים את המוצר מהסבב, כמו שאין לו תמונה מלכתחילה.
  const [brokenSkus, setBrokenSkus] = useState<ReadonlySet<string>>(new Set());
  const [index, setIndex] = useState(0);
  const [pausedByUser, setPausedByUser] = useState(false);
  const [pausedByPointer, setPausedByPointer] = useState(false);
  const navigate = useNavigate();
  const reducedMotion = useReducedMotionConfig();

  const visibleProducts = useMemo(
    () => products?.filter((p) => !brokenSkus.has(p.sku)) ?? null,
    [products, brokenSkus],
  );

  useEffect(() => {
    let cancelled = false;
    listProducts({})
      .then((result) => {
        if (!cancelled) setProducts(result.filter((p) => p.image !== null));
      })
      .catch((err) => {
        // ⚠️ ⛔ לא קריטי — ה-hero פשוט לא מציג תצוגה, שאר הדף תקין.
        if (!isPublicApiError(err)) {
          // אין מה לעשות עם השגיאה מעבר לזה; אין UI ייעודי לכשל תצוגת ה-hero.
        }
        if (!cancelled) setProducts([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ⭐ "הבאות בתור נטענות ברקע אחרי ה-load" (5.2.1) — חימום מטמון הדפדפן
  // לתמונות הבאות בתור בלי לחסום את ה-LCP של התמונה הראשונה.
  useEffect(() => {
    if (!products || products.length <= 1) return;
    const timer = window.setTimeout(() => {
      for (const p of products.slice(1)) {
        if (!p.image) continue;
        const img = new window.Image();
        img.src = p.image.cardUrl;
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [products]);

  const hasMultiple = Boolean(visibleProducts) && (visibleProducts?.length ?? 0) > 1;
  const rotating = hasMultiple && !reducedMotion && !pausedByUser && !pausedByPointer;

  // ⭐ 6.8ג — עולה על כל ניווט ידני (חץ), נכנס לתלות האפקט למטה כדי
  // שהטיימר האוטומטי יתחיל מחדש מלא-4-שניות אחרי לחיצה, ⛔ לא ימשיך לספור
  // מאיפה שהיה ויקפוץ שוב מיד.
  const [resetKey, setResetKey] = useState(0);

  useEffect(() => {
    if (!rotating || !visibleProducts) return;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % visibleProducts.length);
    }, ROTATE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [rotating, visibleProducts, resetKey]);

  function goTo(nextIndex: number) {
    if (!visibleProducts || visibleProducts.length === 0) return;
    setIndex((nextIndex + visibleProducts.length) % visibleProducts.length);
    setResetKey((k) => k + 1);
  }

  // ⭐ כשמוצר יוצא מהסבב (תמונה נכשלה) המערך מתקצר - `index` שהצביע על הסוף
  // עלול להצביע עכשיו מעבר לגבול המערך. בלי זה: מסך ריק לגמרי, ⛔ לא רק
  // חזרה למוצר הראשון.
  useEffect(() => {
    if (!visibleProducts || visibleProducts.length === 0) return;
    if (index >= visibleProducts.length) setIndex(0);
  }, [visibleProducts, index]);

  // ⭐ פרלקסת hero (וריאציה C) — על הכרטיס כולו, טווח מוגבל 14px± (DESIGN.md 6.2).
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });
  const rawY = useTransform(scrollYProgress, [0, 1], [-PARALLAX_RANGE_PX, PARALLAX_RANGE_PX]);
  const parallaxY = useSpring(rawY, { stiffness: 300, damping: 30 });

  // ⚠️ ⛔ **`useScroll({ target: containerRef })` כבר רץ למעלה, בלי תנאי** —
  //    חוק ה-hooks של React. לכן ה-`motion.div` שנושא את ה-ref **חייב** להיות
  //    ממורנדר תמיד, גם כשאין עדיין תוכן — אחרת ה-ref נשאר לא-מחובר וה-frame
  //    loop של `motion` זורק `"Target ref is defined but not hydrated"`
  //    (נתפס בפועל ב-`HomePage.test.tsx`, ⛔ לא רק תיאורטית).
  const current = visibleProducts?.[index];
  const showControls = hasMultiple && !reducedMotion;

  return (
    <motion.div
      ref={containerRef}
      style={{ y: reducedMotion ? 0 : parallaxY }}
      onMouseEnter={() => setPausedByPointer(true)}
      onMouseLeave={() => setPausedByPointer(false)}
      onFocus={() => setPausedByPointer(true)}
      onBlur={() => setPausedByPointer(false)}
    >
      {products === null && (
        <div
          aria-hidden="true"
          className="w-full animate-pulse rounded-lg border border-line bg-surface"
          style={{ aspectRatio: '4 / 3' }}
        />
      )}

      {products !== null && current?.image && (
        <div
          role="region"
          aria-roledescription="carousel"
          aria-label="המוצרים שלנו"
          className="relative"
        >
          <button
            type="button"
            onClick={() => navigate(`/catalog/${current.categorySlug}/${current.sku}`)}
            className="relative block w-full overflow-hidden rounded-lg border border-line bg-canvas text-start"
            style={{ boxShadow: 'var(--shadow-card)', aspectRatio: '4 / 3' }}
          >
            <span className="sr-only">מעבר למוצר {current.name}</span>
            <AnimatePresence initial={false}>
              <motion.img
                key={current.sku}
                src={current.image.cardUrl}
                alt={current.image.alt || current.name}
                loading={index === 0 ? 'eager' : 'lazy'}
                onError={() => {
                  const sku = current.sku;
                  setBrokenSkus((prev) => (prev.has(sku) ? prev : new Set(prev).add(sku)));
                }}
                initial={{ opacity: 0, scale: reducedMotion ? 1 : 1.02 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: reducedMotion ? 0 : CROSSFADE_SECONDS,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="absolute inset-0 h-full w-full object-contain p-6"
              />
            </AnimatePresence>
          </button>

          {/* ⭐ 6.8ג — חיצים ידניים, בנוסף (⛔ לא במקום) לדילוג האוטומטי.
              ⚠️ כיווניות הפוכה מ-LTR בכוונה, עקבי עם `Lightbox.tsx`: "הקודם"
              מצביע ימינה (`CaretRight`, ממוקם ב-`start` = ימין ב-RTL). */}
          {showControls && (
            <>
              <button
                type="button"
                onClick={() => goTo(index - 1)}
                aria-label="המוצר הקודם"
                className="start-2 top-1/2 absolute flex -translate-y-1/2 items-center justify-center rounded-full border border-line bg-canvas text-ink"
                style={{ minWidth: 44, minHeight: 44, boxShadow: 'var(--shadow-card)' }}
              >
                <CaretRight size={16} weight="regular" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => goTo(index + 1)}
                aria-label="המוצר הבא"
                className="end-2 top-1/2 absolute flex -translate-y-1/2 items-center justify-center rounded-full border border-line bg-canvas text-ink"
                style={{ minWidth: 44, minHeight: 44, boxShadow: 'var(--shadow-card)' }}
              >
                <CaretLeft size={16} weight="regular" aria-hidden="true" />
              </button>
            </>
          )}

          <div className="mt-3 flex items-center justify-between gap-3">
            {/* ⚠️ ⛔ **`mode="wait"` חובה כאן, בשונה מ-`AnimatePresence` התמונה
                למעלה.** התמונה עוטפת `absolute inset-0` (שני עותקים חופפים
                בזמן הדעיכה לא זזים). ה-`<p>` כאן הוא פריט flex רגיל בזרימה
                (`justify-between` מול כפתור ההשהיה) — בלי `mode="wait"`,
                במהלך ה-crossfade יש רגעית שני `<p>` בו-זמנית באותה שורת
                flex (שלושה פריטים במקום שניים), מה שדוחף את הכיתוב הנכנס
                אל מחוץ לצד ימין עד שהישן מסיים לצאת ומתאפס למקום הנכון —
                בדיוק הבאג שדווח (16.08.2026). */}
            <AnimatePresence mode="wait" initial={false}>
              <motion.p
                key={current.sku}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: reducedMotion ? 0 : CROSSFADE_SECONDS }}
                className="font-medium text-ink"
              >
                {current.name}
              </motion.p>
            </AnimatePresence>

            {showControls && (
              <button
                type="button"
                onClick={() => setPausedByUser((p) => !p)}
                aria-pressed={pausedByUser}
                aria-label={pausedByUser ? 'המשך הצגת המוצרים' : 'עצירת הצגת המוצרים'}
                className="flex shrink-0 items-center justify-center rounded-full border border-line bg-canvas text-ink"
                style={{ minWidth: 36, minHeight: 36 }}
              >
                {pausedByUser ? (
                  <Play size={14} weight="regular" aria-hidden="true" />
                ) : (
                  <Pause size={14} weight="regular" aria-hidden="true" />
                )}
              </button>
            )}
          </div>

          {showControls && (
            <div
              className="mt-2 flex items-center gap-1"
              role="group"
              aria-label="בחירת מוצר להצגה"
            >
              {visibleProducts?.map((p, i) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setIndex(i)}
                  aria-label={`מוצר ${i + 1} מתוך ${visibleProducts.length}`}
                  aria-current={i === index}
                  className="flex shrink-0 items-center justify-center rounded-full"
                  style={{ minWidth: 24, minHeight: 24 }}
                >
                  <span
                    aria-hidden="true"
                    className="block h-2 w-2 rounded-full"
                    style={{
                      backgroundColor: i === index ? 'var(--color-copper)' : 'var(--color-line)',
                    }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}
